(() => {
  'use strict';
  if (customElements.get('ruemood-pairings')) return;
  const root = window.Shopify?.routes?.root || '/';
  const qa = new URLSearchParams(location.search).get('rm_pairing_qa') === '1';
  const consent = () => window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
  const seen = new Set();
  let cartItems = null;
  let cartRead;
  const cartIds = () => new Set((cartItems || []).map(item => String(item.product_id)));
  async function readCart() {
    if (!cartRead) cartRead = fetch(root + 'cart.js', {cache:'no-store'}).then(r => {
      if (!r.ok) throw new Error('Cart unavailable');
      return r.json();
    }).then(cart => { cartItems = cart.items; return cart; }).finally(() => { cartRead = null; });
    return cartRead;
  }
  function publish(kind, block, card, variant, extra = {}) {
    if (card.hasAttribute('data-outfit-source')) return false;
    // Shopify's pixel manager and the receiver enforce consent. Publishing an
    // allowlisted event is not a separate network request or analytics store.
    const data = {
      source_product: block.dataset.source, target_product: card.dataset.pairId,
      placement: block.dataset.placement, pairing_version: block.dataset.version,
      position: Number(card.dataset.position), currency: block.dataset.currency,
      variant_id: variant ? String(variant.id) : '', value: variant ? variant.price / 100 : 0,
      qa, ...(block.dataset.lookId ? {look_id:block.dataset.lookId} : {}), ...extra
    };
    try { window.Shopify.analytics.publish('ruemood_pairing_' + kind, data); return true; } catch (_) { return false; }
  }
  class Pairings extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.cards = [...this.querySelectorAll('[data-pair-id]')];
      this.timers = new Map();
      this.cards.forEach(card => {
        card.variants = JSON.parse(card.querySelector('[data-pair-variants]').textContent);
        card.controls = [...card.querySelectorAll('[data-pair-option]')];
        card.controls.forEach(select => {
          // Curate colour here; the outfit helper can suggest sizes from an approved product guide.
          if (/^(color|colour|design)$/i.test(select.dataset.optionName) && card.dataset.preferred) select.value = card.dataset.preferred;
          select.addEventListener('change', () => this.update(card, true));
          select.addEventListener('keydown', e => { if (e.key === 'Enter') e.preventDefault(); });
        });
        this.prepareQuickshop(card);
        card.querySelector('[data-pair-choose]').addEventListener('click', e => {
          e.preventDefault();
          if (this.outfit) { this.outfit.chooseOrAdd(card,e.currentTarget); return; }
          if (card.querySelector('.rm-quickshop')) { this.openOptions(card,e.currentTarget); return; }
          const button = e.currentTarget, panel = card.querySelector('.rm-pair__options');
          panel.hidden = !panel.hidden; button.setAttribute('aria-expanded', String(!panel.hidden));
          button.querySelector('span').textContent = panel.hidden ? '+' : '−';
          if (!panel.hidden) { card.controls.find(s => !s.value)?.focus(); publish('options', this, card, card.variant); }
        });
        card.querySelectorAll('[data-pair-link]').forEach(link => link.addEventListener('click', e => {
          if (link.hasAttribute('data-pair-quick-open') && card.querySelector('.rm-quickshop') && !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey) { e.preventDefault(); this.openOptions(card,link); return; }
          publish('click', this, card, card.variant);
        }));
        card.querySelector('[data-pair-add]').addEventListener('click', () => this.add(card));
        this.update(card);
      });
      if (this.hasAttribute('data-outfit') && window.RuemoodOutfit) this.outfit = new window.RuemoodOutfit(this, {readCart, publish, consent, qa, root});
      this.dataset.ready = 'true';
      this.observe();
      if (!window.Shopify?.customerPrivacy && window.Shopify?.loadFeatures) window.Shopify.loadFeatures([{name:'consent-tracking-api',version:'0.1'}], () => this.observe());
      this.onConsent = () => this.observe();
      document.addEventListener('visitorConsentCollected', this.onConsent);
      readCart().then(() => this.excludeCart()).catch(() => {});
      // Keep Shopify's native recommendation parameters intact for click attribution.
      fetch(this.dataset.recommendationsUrl + '.json?product_id=' + this.dataset.source + '&intent=complementary&limit=10')
        .then(r => r.ok ? r.json() : Promise.reject()).then(data => {
          this.cards.forEach(card => {
            const product = data.products?.find(p => String(p.id) === card.dataset.pairId);
            if (product?.url) card.querySelectorAll('[data-pair-link]').forEach(a => {
              const url = new URL(product.url, location.origin);
              if (qa) url.searchParams.set('rm_pairing_qa','1');
              a.href = url.pathname + url.search;
            });
          });
        }).catch(() => {});
    }
    prepareQuickshop(card) {
      if (!card.quickshop && card.querySelector('.rm-quickshop') && window.RuemoodQuickshop) {
        card.quickshop = new window.RuemoodQuickshop(card, {
          update: () => this.update(card, true),
          opened: () => publish('options', this, card, card.variant)
        });
      }
      return card.quickshop;
    }
    openOptions(card,trigger) {
      const existing = card.quickshop;
      if (!this.prepareQuickshop(card)) {
        let status = card.querySelector('[data-pair-open-status]');
        if (!status) {
          status = document.createElement('p'); status.dataset.pairOpenStatus = '';
          status.className = 'rm-pair__status'; status.setAttribute('role','status');
          card.querySelector('.rm-pair__body').append(status);
        }
        status.textContent = 'Product options are still loading. Please try again in a moment.';
        return false;
      }
      card.querySelector('[data-pair-open-status]')?.remove();
      if (!existing) this.update(card);
      card.quickshop.open(trigger);
      return card.quickshop.dialog.open;
    }
    disconnectedCallback() {
      this.outfit?.destroy();
      this.observer?.disconnect(); this.timers?.forEach(clearTimeout);
      this.cards?.forEach(card => card.quickshop?.destroy());
      document.removeEventListener('visitorConsentCollected', this.onConsent);
    }
    update(card, clearStatus = false) {
      const choices = card.controls.map(s => s.value);
      card.variant = card.controls.length ? card.variants.find(v => v.options.every((value,i) => value === choices[i])) : card.variants[0];
      const possible = card.variants.filter(v => v.available && choices.every((value,i) => !value || v.options[i] === value));
      // Disable impossible combinations without silently changing a chosen size.
      card.controls.forEach((select,i) => [...select.options].forEach(option => {
        option.disabled = !!option.value && !card.variants.some(v => v.available && v.options[i] === option.value && choices.every((value,j) => j === i || !value || v.options[j] === value));
      }));
      const button = card.querySelector('[data-pair-add]');
      button.disabled = !card.variant?.available || !!card.busy || card.dataset.added === 'true';
      button.textContent = card.dataset.added === 'true' ? 'Added to bag' : card.variant && !card.variant.available ? 'Sold out' : 'Add to bag';
      const display = card.variant || possible[0];
      if (display) {
        const prices = possible.map(v => v.price);
        card.querySelector('[data-pair-price]').textContent = (!card.variant && new Set(prices).size > 1 ? 'From ' : '') + (card.variant || possible.reduce((a,b)=>a.price<b.price?a:b)).money;
        if (display.image) card.querySelectorAll('.rm-pair__image img').forEach(img => { img.src = display.image; img.removeAttribute('srcset'); });
        const quickPrice = card.querySelector('[data-quick-price]');
        if (quickPrice) quickPrice.textContent = card.querySelector('[data-pair-price]').textContent;
      }
      card.quickshop?.sync(display);
      this.outfit?.sync();
      if (clearStatus && !card.busy && card.dataset.added !== 'true') card.querySelector('[data-pair-status]').textContent = choices.every(Boolean) && !card.variant?.available ? 'This combination is unavailable. Choose another option.' : '';
    }
    excludeCart() {
      if (this.outfit) { this.outfit.reconcile(cartItems || []); return; }
      const ids = cartIds();
      this.cards.forEach(card => { if (!card.busy && card.dataset.added !== 'true') card.hidden = ids.has(card.dataset.pairId); });
      this.hidden = this.cards.every(card => card.hidden);
    }
    observe() {
      this.observer?.disconnect(); this.timers?.forEach(clearTimeout); this.timers?.clear();
      if (!('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver(entries => entries.forEach(entry => {
        const card = entry.target, key = [this.dataset.source,this.dataset.placement,this.dataset.lookId || '',card.dataset.pairId].join(':');
        clearTimeout(this.timers.get(card));
        if (entry.intersectionRatio >= .5 && !card.hidden && !seen.has(key)) this.timers.set(card, setTimeout(() => {
          if (document.visibilityState === 'visible' && !this.hidden && !card.hidden && publish('view',this,card,card.variant)) seen.add(key);
        },1000));
      }), {threshold:[0,.5]});
      this.cards.forEach(card => this.observer.observe(card));
    }
    async add(card) {
      if (card.busy || !card.variant?.available || card.dataset.added === 'true') return;
      card.busy = true;
      const button = card.querySelector('[data-pair-add]'), status = card.querySelector('[data-pair-status]');
      button.disabled = true; button.textContent = 'Adding…'; status.textContent = '';
      const variant = card.variant;
      try {
        await readCart();
        if (cartIds().has(card.dataset.pairId)) { status.textContent = 'This piece is already in your bag.'; card.dataset.added = 'true'; card.quickshop?.added(); return; }
        const properties = consent() ? {_rm_pairing_source:this.dataset.source,_rm_pairing_version:this.dataset.version,_rm_pairing_placement:this.dataset.placement,_rm_pairing_position:card.dataset.position,...(qa ? {_rm_pairing_qa:'1'} : {})} : {};
        // No retries: a failed response must never create a duplicate add.
        const response = await fetch(root + 'cart/add.js', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({items:[{id:variant.id,quantity:1,properties}]})});
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : 'This piece could not be added. Please try again.');
        card.dataset.added = 'true'; status.textContent = 'Added to your bag.';
        card.quickshop?.added();
        publish('add',this,card,variant,{quantity:1});
        if (window.cartCoreV2) {
          await window.cartCoreV2.loadCartState().catch(() => {}); if (!card.quickshop) window.cartCoreV2.openDrawer();
        }
        await readCart().catch(() => {});
        document.dispatchEvent(new CustomEvent('ruemood:pairing-cart-updated'));
        if (this.closest('ruemood-cart-pairings')?.dataset.placement === 'cart-page') location.reload();
      } catch (error) {
        status.textContent = error.message || 'Your bag could not be updated. Check your connection and try again.';
        publish('error',this,card,variant,{error_type:'add_failed'});
      } finally { card.busy = false; this.update(card); }
    }
  }
  customElements.define('ruemood-pairings', Pairings);
  class CartPairings extends HTMLElement {
    connectedCallback() {
      this.onUpdate = () => this.schedule();
      document.addEventListener('ruemood:pairing-cart-updated', this.onUpdate);
      this.connectCore();
      if (this.dataset.placement === 'cart-page') this.refresh();
    }
    connectCore() {
      if (!this.isConnected) return;
      if (!window.cartCoreV2) { this.connectTimer = setTimeout(() => this.connectCore(),250); return; }
      this.onState = state => { if (this.dataset.placement === 'cart-page' || state.isOpen) this.schedule(); };
      window.cartCoreV2.on('cart:state-changed',this.onState);
      window.cartCoreV2.on('cart:drawer-opened',this.onUpdate);
    }
    disconnectedCallback() { clearTimeout(this.timer); clearTimeout(this.connectTimer); document.removeEventListener('ruemood:pairing-cart-updated',this.onUpdate); window.cartCoreV2?.off?.('cart:state-changed',this.onState); window.cartCoreV2?.off?.('cart:drawer-opened',this.onUpdate); }
    schedule() { clearTimeout(this.timer); this.timer=setTimeout(()=>this.refresh(),150); }
    async refresh() {
      const seq = this.seq = (this.seq || 0)+1;
      try {
        const cart = await readCart();
        document.querySelectorAll('ruemood-pairings').forEach(block => block.excludeCart?.());
        const signature = JSON.stringify([cart.currency,cart.items.map(i=>[i.product_id,i.variant_id,i.quantity])]);
        if (signature === this.signature) return;
        if (!cart.items.length) { this.replaceChildren(); this.signature=signature; return; }
        const response = await fetch(root+'cart?section_id=ruemood-cart-pairings',{cache:'no-store'});
        if (!response.ok) return;
        const text = await response.text();
        if (seq !== this.seq || !this.isConnected) return;
        const dom = new DOMParser().parseFromString(text,'text/html'), block = dom.querySelector('ruemood-pairings');
        this.replaceChildren(...(block ? [block] : [])); this.signature = signature;
      } catch (_) { /* Suggestions never block the cart. */ }
    }
  }
  customElements.define('ruemood-cart-pairings',CartPairings);
})();
